import {
  ExtractPayload,
  action$,
  ofType,
  persistentReducedStream,
  reducer,
  spreadMap,
  streamReducer,
} from '@ardoq/rxbeach';
import {
  notifyScenarioChanged,
  notifyWorkspaceChanged,
  notifyWorkspaceClosed,
  notifyContextChanged,
} from './ContextActions';
import { ContextShape } from '@ardoq/data-model';
import { toggleExploreMode } from 'presentation/actions';
import {
  debounceTime,
  distinctUntilKeyChanged,
  filter,
  map,
  merge,
  pairwise,
  withLatestFrom,
} from 'rxjs';
import { sort$ } from './sort$';
import { ArdoqId } from '@ardoq/api-types';
import { difference } from 'lodash';
import { type ContextSort, getDefaultSort } from '@ardoq/common-helpers';
import { context as Context } from 'context';

export const emptyContext = (): ContextShape => ({
  componentId: '',
  referenceId: '',
  workspaceId: '',
  scenarioId: '',
  workspaceIds: [],
  presentationId: '',
  isExploreMode: false,
  sort: getDefaultSort(),
});

const toggleExploreModeReducer = (
  state: ContextShape,
  { isExploreMode }: ExtractPayload<typeof toggleExploreMode>
) => ({
  ...state,
  isExploreMode,
});

const dependencies$ = merge(
  action$.pipe(
    ofType(
      notifyScenarioChanged,
      notifyWorkspaceClosed,
      notifyWorkspaceChanged,
      toggleExploreMode,
      notifyContextChanged
    )
  ),
  sort$
).pipe(debounceTime(16));

export const makeContext = (sort: ContextSort): ContextShape => {
  const componentId = Context.componentId() ?? '';
  const referenceId = Context.referenceId() || '';
  const workspaceId = Context.activeWorkspaceId() || '';
  const scenarioId = Context.scenarioId() ?? '';
  const workspaceIds = Context.workspaceIds();
  const presentationId = Context.presentationId() || '';
  return {
    componentId,
    referenceId,
    workspaceId,
    scenarioId,
    workspaceIds,
    presentationId,
    isExploreMode: Context.isExploreMode(),
    sort: { ...sort },
  };
};

const rebuildContext = (
  _: ContextShape,
  [, sort]: [unknown, ContextSort]
): ContextShape => makeContext(sort);

export const context$ = persistentReducedStream(
  'context$',
  makeContext(getDefaultSort()),
  [
    streamReducer(dependencies$.pipe(withLatestFrom(sort$)), rebuildContext),
    reducer(toggleExploreMode, toggleExploreModeReducer),
  ]
);

export const contextWorkspaceId$ = context$.pipe(
  distinctUntilKeyChanged('workspaceId')
);

export const contextComponentId$ = context$.pipe(
  distinctUntilKeyChanged('componentId')
);

export const contextWorkspaceOrComponentId$ = merge(
  contextWorkspaceId$,
  contextComponentId$
);

export const contextPresentationId$ = context$.pipe(
  distinctUntilKeyChanged('presentationId')
);

export const contextReferenceId$ = context$.pipe(
  distinctUntilKeyChanged('referenceId')
);

export type WorkspaceChanges = {
  workspaceIds: ArdoqId[];
  opened: ArdoqId[];
  closed: ArdoqId[];
};

const computeWorkspaceChanges = (
  previous: ContextShape,
  current: ContextShape
): WorkspaceChanges => ({
  workspaceIds: current.workspaceIds,
  opened: difference(current.workspaceIds, previous.workspaceIds),
  closed: difference(previous.workspaceIds, current.workspaceIds),
});

export const workspaceChanges$ = context$.pipe(
  distinctUntilKeyChanged('workspaceIds'),
  pairwise(),
  spreadMap(computeWorkspaceChanges)
);

export const workspacesOpened$ = workspaceChanges$.pipe(
  filter(({ opened }) => opened.length > 0),
  map(({ opened }) => opened)
);

export const workspacesClosed$ = workspaceChanges$.pipe(
  filter(({ closed }) => closed.length > 0)
);
